import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import client from "../../../../API";
import {
  Box,
  Typography,
  Button,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  Container,
  TextField,
  List,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
  Chip,
  Avatar,
  AvatarGroup,
  Tooltip,
  Stack,
  ButtonGroup,
  useMediaQuery,
  Divider,
  Paper,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon2 from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import GoogleIcon from "@mui/icons-material/Google";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BarChartIcon from "@mui/icons-material/BarChart";

import useIsDesktop from "../../../../Hooks/useIsDesktop.js";
import useAuth from "../../../../Hooks/use-auth.js";
import SelectAssetDialog from "../../../../Components/tools/selectfile";
import PageTitle from "../../../../Components/PageTitle";
import { parseLocalFloat, parseLocalInt } from "../../../../Utils/constants.js";
import BrandNegotiationDialog from "./brandnegotiatedialog.js";
import ShareCampaignDialog from "./share.js";
import CreatorRow from "./creatorRow.js";
import useAlertDialog from "../../../../Components/useAlertDialog.js";
import AlertDialog from "../../../../Components/AlertDialog.js";
import CampaignVideoEditor from "../../../../Components/CampaignVideoEditor.js";
import CampaignInternalTracker from "./extras/campaigninternaltracker.js";

import {
  Settings,
  Launch,
  Save,
  Close as CloseIcon,
  TaskAlt,
  Menu as MenuIcon,
} from "@mui/icons-material";

import { GradientButton, StatusChip } from "./components/styledcomps.js";
import { COMPLETE_CAMPAIGN_STEPS } from "./components/campaignconstants.js";

/* ─── tiny stat pill ─────────────────────────────────────────────── */
function StatPill({ icon, label, value, color }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: 99,
        background: alpha(color || theme.palette.primary.main, 0.08),
        border: `1px solid ${alpha(color || theme.palette.primary.main, 0.18)}`,
        whiteSpace: "nowrap",
      }}
    >
      {icon &&
        React.cloneElement(icon, {
          sx: { fontSize: 13, color: color || theme.palette.primary.main },
        })}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: color || theme.palette.primary.main,
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      {label && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", lineHeight: 1, fontSize: "0.68rem" }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
}

/* ─── inline editable brief ──────────────────────────────────────── */
function InlineBrief({ brief, isEditable, editedBrief, setEditedBrief, onSave }) {
  const [editing, setEditing] = useState(false);
  const theme = useTheme();

  if (!editing) {
    return (
      <Typography
        variant="body2"
        onClick={() => isEditable && setEditing(true)}
        sx={{
          color: "text.secondary",
          cursor: isEditable ? "pointer" : "default",
          maxWidth: 640,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          lineHeight: 1.5,
          "&:hover": isEditable
            ? {
                color: "text.primary",
                textDecoration: "underline dotted",
                textUnderlineOffset: 3,
              }
            : {},
        }}
      >
        {brief || (isEditable ? "Click to add a brief…" : "No brief.")}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, maxWidth: 640 }}>
      <TextField
        value={editedBrief}
        onChange={(e) => setEditedBrief(e.target.value)}
        multiline
        minRows={2}
        maxRows={4}
        size="small"
        autoFocus
        fullWidth
        sx={{
          "& .MuiOutlinedInput-root": {
            fontSize: "0.875rem",
            "& fieldset": { borderColor: alpha("#dc2626", 0.3) },
            "&.Mui-focused fieldset": { borderColor: "#dc2626" },
          },
        }}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => {
            onSave(editedBrief);
            setEditing(false);
          }}
          sx={{ bgcolor: alpha("#16a34a", 0.1), color: "#16a34a" }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setEditing(false)}
          sx={{ bgcolor: alpha("#6b7280", 0.1), color: "#6b7280" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

/* ─── main component ─────────────────────────────────────────────── */
const EditCampaignPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useIsDesktop();
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();
  const isTcc = currentUser?.company_name === "TCC";
  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  const [campaignDetails, setCampaignDetails] = useState(null);
  const [isEditingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedBrief, setEditedBrief] = useState("");
  const [creatorsToRemove, setCreatorsToRemove] = useState([]);
  const [showLaunchConfirmDialog, setShowLaunchConfirmDialog] = useState(false);
  const [showCompleteConfirmDialog, setShowCompleteConfirmDialog] = useState(false);
  const [showAddCreatorDialog, setShowAddCreatorDialog] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [expandedCreator, setExpandedCreator] = useState(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [currentCreatorIndex, setCurrentCreatorIndex] = useState(null);
  const [negotiationDialogOpen, setNegotiationDialogOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoDialogCreator, setVideoDialogCreator] = useState(null);
  const [dropCreatorDialogOpen, setDropCreatorDialogOpen] = useState(false);
  const [creatorToDrop, setCreatorToDrop] = useState(null);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [selectedCreatorForChat, setSelectedCreatorForChat] = useState(null);

  const { isFetching, data, refetch: refetchCampaign } = useQuery(
    ["campaign", campaignId, "details"],
    () => client.campaigns.details(campaignId),
    { refetchOnWindowFocus: false }
  );

  const { data: files, refetch: refetchFiles } = useQuery("files", () => client.files.fetch(), {
    refetchOnWindowFocus: false,
  });

  const { mutate: submitNegotiationResponse, isLoading: isNegotiationSubmitting } = useMutation(
    (payload) => client.campaigns.handleNegotiationResponse(campaignId, payload),
    {
      onSuccess: () => {
        setNegotiationDialogOpen(false);
        setSelectedCreator(null);
        refetchCampaign();
        queryClient.invalidateQueries(["campaign", campaignId, "details"]);
      },
      onError: (e) =>
        openDialog(
          "Negotiation error",
          e?.response?.data?.error || e?.message || "Request failed",
          closeDialog,
          null,
          "Ok"
        ),
    }
  );

  const campaignMetrics = useMemo(() => {
    if (!campaignDetails) return null;
    const statusCounts = {};
    campaignDetails.creators.forEach((c) => {
      const s = c.status || "Unknown";
      statusCounts[s] = (statusCounts[s] || 0) + 1;
    });
    const totalSpend = campaignDetails.creators.reduce(
      (a, c) => a + parseFloat(c.agencyRate || c.price || 0),
      0
    );
    const rawTotalSpend = campaignDetails.creators.reduce(
      (a, c) => a + parseFloat(c.price || 0),
      0
    );
    const totalViews = campaignDetails.creators.reduce(
      (acc, cr) =>
        acc + cr.videos.reduce((vAcc, v) => vAcc + (parseLocalInt(v.totalViews) || 0), 0),
      0
    );
    const totalEngagement = campaignDetails.creators.reduce(
      (acc, cr) =>
        acc +
        cr.videos.reduce(
          (eAcc, v) =>
            eAcc + (parseLocalInt(v.likes) || 0) + (parseLocalInt(v.comments) || 0),
          0
        ),
      0
    );
    const approvedCount = statusCounts.Approved || 0;
    const createdAt = new Date(
      campaignDetails.created_at || campaignDetails.createdAt || campaignDetails.created
    );
    const launchedAt = campaignDetails.launch_date ? new Date(campaignDetails.launch_date) : null;
    const sinceDate = launchedAt || createdAt;
    const daysSince = Math.floor((Date.now() - sinceDate.getTime()) / 86_400_000);

    return {
      statusCounts,
      totalSpend,
      rawTotalSpend,
      totalViews,
      totalEngagement,
      approvedCount,
      creatorCount: campaignDetails.creators.length,
      daysSince,
      daysSinceLaunch: Boolean(launchedAt),
    };
  }, [campaignDetails]);

  useEffect(() => {
    if (data) {
      setCampaignDetails({
        ...data,
        creators: data.creators.map((creator) => ({
          ...creator,
          price: typeof creator?.price === "number" ? creator.price.toFixed(2) : "",
          agencyRate:
            typeof creator?.agencyRate === "number" ? creator.agencyRate.toFixed(2) : "",
          notifications: creator.notifications || [],
        })),
      });
      setEditedName(data.name || "");
      setEditedBrief(data.brief || "");
      setUnsavedChanges(false);
    }
  }, [data]);

  const { mutate: updateCampaignCreatorSpecs, isLoading: isLoadingUpateSpecs } = useMutation(
    (payload) => client.campaigns.updateCampaignCreatorSpecs(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["campaign", campaignId]);
        refetchCampaign();
        alert("Creator details updated successfully!");
      },
      onError: (error) => alert(error.response?.data?.error || error.message),
    }
  );

  const { mutate: updateMetaData, isLoading: isLoadingUpdateMetadata } = useMutation(
    (payload) => client.campaigns.updateMetaData(payload),
    {
      onSuccess: () => queryClient.invalidateQueries(["campaign", campaignId]),
      onError: (error) => alert(error.response?.data?.error || error.message),
    }
  );

  const { mutate: resendOfferMutation } = useMutation(
    ({ campaignId, creatorName }) => client.campaigns.resendOffer(campaignId, creatorName),
    {
      onSuccess: (data) => alert(data.message),
      onError: (err) => alert(err.response?.data?.error || err.message),
    }
  );

  const { mutate: launchCampaign, isLoading: isLoadingLaunch } = useMutation(
    (payload) => client.campaigns.launch(payload),
    {
      onSuccess: () => {
        alert("Campaign launched successfully!");
        queryClient.invalidateQueries(["campaign", campaignId]);
      },
      onError: (error) => alert(error.response?.data?.error || error.message),
    }
  );

  const { mutate: completeCampaign, isLoading: isLoadingComplete } = useMutation(
    (input) => client.campaigns.complete(input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["campaign", campaignId]);
        alert("Campaign completed successfully!");
      },
      onError: (error) => alert(error.response?.data?.error || error.message),
    }
  );

  const { mutate: completeCreatorMutation } = useMutation(
    (input) => client.campaigns.completeCreator(campaignId, input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["campaign", campaignId]);
        alert("Creator completed successfully!");
      },
      onError: (error) => alert(error.response?.data?.error || error.message),
    }
  );

  const { mutate: completeVideoMutation } = useMutation(
    (input) => client.campaigns.completeVideo(campaignId, input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["campaign", campaignId]);
        alert("Video completed successfully!");
      },
      onError: (error) => alert(error.response?.data?.error || error.message),
    }
  );

  const handleFieldChange = (creatorId, field, value, markUnsaved = true) => {
    const updatedCreators = campaignDetails.creators.map((creator) =>
      creator.id === creatorId ? { ...creator, [field]: value } : creator
    );
    setCampaignDetails((prev) => ({ ...prev, creators: updatedCreators }));
    if (markUnsaved) setUnsavedChanges(true);
  };

  const saveChanges = () => {
    const payload = {
      campaignId,
      creators: campaignDetails.creators.map((creator) => ({
        id: creator.id,
        status: creatorsToRemove.includes(creator.id)
          ? "Dropped"
          : creator.assetsApproved
          ? "Approved"
          : creator.status,
        price: parseLocalFloat(creator?.price),
        agencyRate: parseLocalFloat(creator?.agencyRate),
        assetsSent: creator.assetsSent,
        assetsApproved: creator.assetsApproved,
        skipPayout: creator.skipPayout,
        creatorBrief: creator.creatorBrief,
        postingInstructions: creator.postingInstructions,
        boostCode: creator.boostCode,
        submissionLink: creator.submissionLink,
        videos: creator.videos,
      })),
    };
    updateCampaignCreatorSpecs(payload);
    setUnsavedChanges(false);
  };

  const handleConfirmLaunch = async () => {
    setIsLaunching(true);
    setShowLaunchConfirmDialog(false);
    const creatorNames = campaignDetails?.creators?.map((c) => c.name) || [];
    const creatorPromotionTypes =
      campaignDetails?.creators?.map((c) => ({ name: c.name, promotionType: c.promotionType })) ||
      [];
    const creatorPrices =
      campaignDetails?.creators?.reduce((acc, c) => {
        acc[c.name] = parseLocalFloat(c.price);
        return acc;
      }, {}) || {};
    const agencyRates =
      campaignDetails?.creators?.reduce((acc, c) => {
        acc[c.name] = parseLocalFloat(c.agencyRate || "0");
        return acc;
      }, {}) || {};
    const payload = {
      campaignId: campaignDetails?.id,
      campaignName: campaignDetails?.name,
      campaignBrief: campaignDetails?.brief,
      creatorNames,
      creatorPromotionTypes,
      creatorPrices,
      agencyRates,
      blitzautocampaign: true,
      idealduedate: campaignDetails?.ideal_duedate,
      userEmail: campaignDetails?.user_email,
      sharedWithEmail: campaignDetails?.shared_with_email,
    };
    try {
      launchCampaign(payload);
    } finally {
      setIsLaunching(false);
    }
  };

  const handleConfirmComplete = async () => {
    setIsCompleting(true);
    setShowCompleteConfirmDialog(false);
    try {
      completeCampaign({ campaignId: campaignDetails?.id });
    } finally {
      setIsCompleting(false);
    }
  };

  if (!campaignDetails) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress size={60} sx={{ color: "#dc2626" }} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading campaign details...
        </Typography>
      </Box>
    );
  }

  const isDraft = campaignDetails?.campaign_status === "Draft";
  const isLaunched = campaignDetails?.campaign_status === "Launched";
  const isCompleted = campaignDetails?.campaign_status === "Completed";
  const isEditable = isTcc || (isDraft && isDesktop);
  const canAddCreators = isDraft || isLaunched;

  const totalSpendFormatted = campaignMetrics
    ? `$${campaignMetrics.totalSpend
        .toFixed(0)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
    : null;

  return (
    <>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={
          isFetching ||
          isLaunching ||
          isCompleting ||
          isLoadingComplete ||
          isLoadingLaunch ||
          isLoadingUpateSpecs ||
          isLoadingUpdateMetadata
        }
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <AlertDialog alertState={dialogState} />
      <PageTitle title={`Edit Campaign - ${campaignDetails.name} - Blitz`} />

      <Container maxWidth="xl" sx={{ py: isMobile ? 1.5 : 3, px: isMobile ? 1 : 3 }}>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* TOP HEADER — name + meta + actions, all in one band       */}
        {/* ══════════════════════════════════════════════════════════ */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: isMobile ? 2 : "20px 28px",
            borderRadius: 3,
            background: `linear-gradient(145deg, ${alpha("#dc2626", 0.04)} 0%, ${alpha(
              theme.palette.background.paper,
              0.97
            )} 60%)`,
            border: `1px solid ${alpha("#dc2626", 0.1)}`,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: 4,
              height: "100%",
              background: "linear-gradient(180deg, #dc2626, #991b1b)",
              borderRadius: "3px 0 0 3px",
            },
          }}
        >
          {/* Row 1 — title + status + actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: isMobile ? "flex-start" : "center",
              gap: 1.5,
              mb: 1.5,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            {/* Name */}
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              {isEditingName ? (
                <TextField
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  variant="outlined"
                  size="small"
                  autoFocus
                  sx={{
                    minWidth: 280,
                    "& .MuiOutlinedInput-root": {
                      fontSize: "1.4rem",
                      fontWeight: 700,
                      "& fieldset": { borderColor: alpha("#dc2626", 0.3) },
                      "&.Mui-focused fieldset": { borderColor: "#dc2626" },
                    },
                  }}
                />
              ) : (
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  sx={{
                    fontWeight: 800,
                    background: "linear-gradient(135deg, #dc2626, #991b1b)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    cursor: isEditable ? "pointer" : "default",
                    wordBreak: "break-word",
                    lineHeight: 1.2,
                  }}
                  onClick={() => isEditable && setEditingName(true)}
                >
                  {campaignDetails.name}
                </Typography>
              )}

              {isEditable && (
                <IconButton
                  size="small"
                  onClick={() => {
                    if (isEditingName) {
                      updateMetaData({
                        id: campaignId,
                        name: editedName,
                        brief: editedBrief,
                        campaign_sum: campaignMetrics?.rawTotalSpend || 0,
                      });
                      setEditingName(false);
                    } else {
                      setEditingName(true);
                    }
                  }}
                  sx={{
                    width: 28,
                    height: 28,
                    background: isEditingName
                      ? alpha("#16a34a", 0.12)
                      : alpha("#dc2626", 0.08),
                    color: isEditingName ? "#16a34a" : "#dc2626",
                    flexShrink: 0,
                  }}
                >
                  {isEditingName ? (
                    <CheckIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <EditIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              )}

              <StatusChip
                label={campaignDetails.campaign_status}
                status={campaignDetails.campaign_status}
                size="small"
              />
            </Box>

            {/* Actions — desktop inline, mobile hamburger */}
            {!isMobile ? (
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }} alignItems="center">
                {unsavedChanges && isEditable && (
                  <GradientButton variant="success" size="small" onClick={saveChanges} startIcon={<Save sx={{ fontSize: 15 }} />}>
                    Save
                  </GradientButton>
                )}
                {isDraft && (
                  <GradientButton variant="rainbow" size="small" onClick={() => setShowLaunchConfirmDialog(true)} startIcon={<Launch sx={{ fontSize: 15 }} />}>
                    Launch
                  </GradientButton>
                )}
                {isLaunched && (
                  <GradientButton variant="complete" size="small" onClick={() => setShowCompleteConfirmDialog(true)} startIcon={<TaskAlt sx={{ fontSize: 15 }} />}>
                    Complete
                  </GradientButton>
                )}
                {canAddCreators && (
                  <Tooltip title="Add creators" arrow>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowAddCreatorDialog(true)}
                      startIcon={<AddCircleIcon sx={{ fontSize: 15 }} />}
                      sx={{ color: "#dc2626", borderColor: alpha("#dc2626", 0.4), borderRadius: 2 }}
                    >
                      Add
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Settings & sharing" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setShareDialogOpen(true)}
                    sx={{
                      border: `1px solid ${alpha("#dc2626", 0.25)}`,
                      color: "#dc2626",
                      borderRadius: 2,
                    }}
                  >
                    <Settings sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                {isDraft && (
                  <GradientButton variant="rainbow" onClick={() => setShowLaunchConfirmDialog(true)} startIcon={<Launch />} fullWidth size="small">
                    Launch
                  </GradientButton>
                )}
                {isLaunched && (
                  <GradientButton variant="complete" onClick={() => setShowCompleteConfirmDialog(true)} startIcon={<TaskAlt />} fullWidth size="small">
                    Complete
                  </GradientButton>
                )}
                {unsavedChanges && isEditable && (
                  <GradientButton variant="success" onClick={saveChanges} startIcon={<Save />} fullWidth size="small">
                    Save
                  </GradientButton>
                )}
                <IconButton
                  size="small"
                  onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
                  sx={{ border: `1px solid ${alpha("#dc2626", 0.25)}`, color: "#dc2626", borderRadius: 2 }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={mobileMenuAnchor}
                  open={Boolean(mobileMenuAnchor)}
                  onClose={() => setMobileMenuAnchor(null)}
                  PaperProps={{ sx: { borderRadius: 2, minWidth: 200 } }}
                >
                  {canAddCreators && (
                    <MenuItem onClick={() => { setShowAddCreatorDialog(true); setMobileMenuAnchor(null); }}>
                      <ListItemIcon><AddCircleIcon sx={{ color: "#dc2626" }} /></ListItemIcon>
                      <ListItemText>Add Creators</ListItemText>
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => { setShareDialogOpen(true); setMobileMenuAnchor(null); }}>
                    <ListItemIcon><Settings sx={{ color: "#dc2626" }} /></ListItemIcon>
                    <ListItemText>Settings</ListItemText>
                  </MenuItem>
                  {campaignDetails.drive_link && (
                    <MenuItem component="a" href={campaignDetails.drive_link} target="_blank" onClick={() => setMobileMenuAnchor(null)}>
                      <ListItemIcon><GoogleIcon sx={{ color: "#dc2626" }} /></ListItemIcon>
                      <ListItemText>Open Drive</ListItemText>
                    </MenuItem>
                  )}
                </Menu>
              </Stack>
            )}
          </Box>

          {/* Row 2 — brief + stat pills in one horizontal band */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            {/* Brief (inline editable) */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <InlineBrief
                brief={campaignDetails.brief}
                isEditable={isEditable}
                editedBrief={editedBrief}
                setEditedBrief={setEditedBrief}
                onSave={(newBrief) =>
                  updateMetaData({
                    id: campaignId,
                    name: editedName,
                    brief: newBrief,
                    campaign_sum: campaignMetrics?.rawTotalSpend || 0,
                  })
                }
              />
            </Box>

            {/* Stat pills */}
            {campaignMetrics && (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.75,
                  flexWrap: "wrap",
                  flexShrink: 0,
                  alignItems: "center",
                }}
              >
                {totalSpendFormatted && (
                  <StatPill
                    icon={<AttachMoneyIcon />}
                    value={totalSpendFormatted}
                    label="budget"
                    color="#16a34a"
                  />
                )}
                <StatPill
                  icon={<PeopleIcon />}
                  value={campaignMetrics.creatorCount}
                  label="creators"
                  color="#2563eb"
                />
                {campaignMetrics.totalViews > 0 && (
                  <StatPill
                    icon={<BarChartIcon />}
                    value={
                      campaignMetrics.totalViews >= 1_000_000
                        ? `${(campaignMetrics.totalViews / 1_000_000).toFixed(1)}M`
                        : campaignMetrics.totalViews >= 1_000
                        ? `${(campaignMetrics.totalViews / 1_000).toFixed(0)}K`
                        : campaignMetrics.totalViews
                    }
                    label="views"
                    color="#7c3aed"
                  />
                )}
                <StatPill
                  icon={<CalendarTodayIcon />}
                  value={`${campaignMetrics.daysSince}d`}
                  label={campaignMetrics.daysSinceLaunch ? "since launch" : "since created"}
                  color="#dc2626"
                />
              </Box>
            )}
          </Box>
        </Paper>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* TRACKER — full-width, right below the header              */}
        {/* ══════════════════════════════════════════════════════════ */}
        <Box sx={{ mb: 2 }}>
          <CampaignInternalTracker
            campaignDetails={campaignDetails}
            campaignId={campaignId}
          />
        </Box>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* CREATORS LIST — flows directly, no extra wrappers         */}
        {/* ══════════════════════════════════════════════════════════ */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            overflow: "hidden",
          }}
        >
          {/* Section label + avatar group */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: isMobile ? 2 : 3,
              py: isMobile ? 1.5 : 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              background: alpha("#dc2626", 0.02),
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem" }}
            >
              Creators
            </Typography>
            <AvatarGroup max={isMobile ? 4 : 8} sx={{ "& .MuiAvatar-root": { width: 26, height: 26, fontSize: "0.7rem" } }}>
              {campaignDetails.creators.map((creator, index) => (
                <Avatar
                  key={creator.id}
                  sx={{ bgcolor: `hsl(${(index * 60) % 360}, 65%, 55%)` }}
                >
                  {creator.name?.charAt(0)}
                </Avatar>
              ))}
            </AvatarGroup>
            <Chip
              label={campaignDetails.creators.length}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                fontWeight: 700,
                bgcolor: alpha("#dc2626", 0.08),
                color: "#dc2626",
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          </Box>

          {/* Creator rows */}
          <List sx={{ width: "100%", p: 0 }}>
            {campaignDetails.creators.map((creator, index) => (
              <CreatorRow
                key={creator.id}
                creator={creator}
                campaignDetails={campaignDetails}
                index={index}
                expandedCreator={expandedCreator}
                isLaunched={isLaunched}
                isTcc={isTcc}
                showSkipPayment={campaignDetails?.creators.some((c) => c.skipPayout === true)}
                handleFieldChange={handleFieldChange}
                handleExpandClick={(creatorId) =>
                  setExpandedCreator(expandedCreator === creatorId ? null : creatorId)
                }
                handleDialogOpen={(index) => {
                  setCurrentCreatorIndex(index);
                  setDialogOpen(true);
                }}
                handleResendOfferClick={(creatorName) =>
                  resendOfferMutation({ campaignId, creatorName })
                }
                confirmDropCreator={(creatorId) => {
                  setCreatorToDrop(creatorId);
                  setDropCreatorDialogOpen(true);
                }}
                startConversation={(creatorId, creatorName) => {
                  setSelectedCreatorForChat({ id: creatorId, name: creatorName });
                  setChatDrawerOpen(true);
                }}
                completeCreator={(creatorId) => completeCreatorMutation({ creator: creatorId })}
                completeVideo={(creatorId, videoId) =>
                  completeVideoMutation({ creator: creatorId, video: videoId })
                }
                setSelectedCreator={setSelectedCreator}
                setNegotiationDialogOpen={setNegotiationDialogOpen}
                handleOpenVideoEditor={(creator) => {
                  setVideoDialogCreator(creator);
                  setVideoDialogOpen(true);
                }}
                saveChanges={saveChanges}
              />
            ))}
          </List>
        </Paper>
      </Container>

      {/* ── Dialogs ────────────────────────────────────────────────── */}

      <Dialog
        open={showLaunchConfirmDialog}
        onClose={() => setShowLaunchConfirmDialog(false)}
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}
      >
        <DialogTitle sx={{ color: "#dc2626" }}>Confirm Launch</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to launch this campaign? This action cannot be undone and will
            start outreach to all creators.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          <Button onClick={() => setShowLaunchConfirmDialog(false)} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <GradientButton variant="primary" onClick={handleConfirmLaunch}>
            Confirm Launch
          </GradientButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showCompleteConfirmDialog}
        onClose={() => setShowCompleteConfirmDialog(false)}
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}
      >
        <DialogTitle sx={{ color: "#16a34a" }}>Complete Campaign</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to complete this campaign? This will:</Typography>
          <Box component="ul" sx={{ mt: 2, pl: 2 }}>
            {COMPLETE_CAMPAIGN_STEPS.map((step, index) => (
              <Typography key={index} component="li" variant={isMobile ? "body2" : "body1"}>
                {step}
              </Typography>
            ))}
          </Box>
          <Typography sx={{ mt: 2, fontWeight: 600, color: "#dc2626" }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          <Button onClick={() => setShowCompleteConfirmDialog(false)} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <GradientButton variant="complete" onClick={handleConfirmComplete}>
            Complete Campaign
          </GradientButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAddCreatorDialog}
        onClose={() => setShowAddCreatorDialog(false)}
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2, minWidth: isMobile ? undefined : 420 } }}
      >
        <DialogTitle sx={{ color: "#dc2626", fontWeight: 700 }}>Add Creators</DialogTitle>
        <DialogContent>
          <Typography>
            You're about to add creators to <strong>{campaignDetails?.name}</strong>.
          </Typography>
          {isLaunched && (
            <Typography sx={{ mt: 1.5, color: "text.secondary", fontSize: "0.9rem" }}>
              This campaign is already live — new creators will receive outreach immediately.
            </Typography>
          )}
          {isDraft && (
            <Typography sx={{ mt: 1.5, color: "text.secondary", fontSize: "0.9rem" }}>
              Creators will be added to your draft and sent offers when you launch.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1.5, gap: 1 }}>
          <Button onClick={() => setShowAddCreatorDialog(false)} sx={{ color: "#666", textTransform: "none" }}>
            Cancel
          </Button>
          <GradientButton
            variant="primary"
            startIcon={<AddCircleIcon />}
            onClick={() => {
              setShowAddCreatorDialog(false);
              navigate(`/add-creators/${campaignId}`);
            }}
          >
            Continue
          </GradientButton>
        </DialogActions>
      </Dialog>

      <SelectAssetDialog
        open={dialogOpen}
        onClose={(confirmed) => {
          if (confirmed && selectedAsset) {
            /* handle */
          }
          setDialogOpen(false);
          setSelectedAsset(null);
        }}
        files={files}
        selectedAsset={selectedAsset}
        onAssetClick={setSelectedAsset}
        refetchFiles={refetchFiles}
      />

      <BrandNegotiationDialog
        open={negotiationDialogOpen}
        handleClose={() => {
          setNegotiationDialogOpen(false);
          setSelectedCreator(null);
        }}
        creator={selectedCreator}
        currentOffer={
          selectedCreator?.pending_counter_offer ?? selectedCreator?.originalCounterOffer ?? 0
        }
        onRespond={(payload) => {
          const { new_status: _ns, ...body } = payload;
          submitNegotiationResponse(body);
        }}
        isSubmitting={isNegotiationSubmitting}
      />

      <ShareCampaignDialog
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          setEmail("");
        }}
        campaignDetails={campaignDetails}
        email={email}
        setEmail={setEmail}
      />

      <CampaignVideoEditor
        campaignId={campaignId}
        isOpen={videoDialogOpen}
        creator={videoDialogCreator}
        onClose={() => {
          setVideoDialogOpen(false);
          refetchCampaign();
        }}
      />

      <Dialog
        open={dropCreatorDialogOpen}
        onClose={() => setDropCreatorDialogOpen(false)}
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}
      >
        <DialogTitle sx={{ color: "#dc2626" }}>Confirm Drop</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to drop this creator? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          <Button onClick={() => setDropCreatorDialogOpen(false)} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (creatorToDrop) handleFieldChange(creatorToDrop, "status", "Dropped");
              setDropCreatorDialogOpen(false);
              setCreatorToDrop(null);
            }}
            sx={{ color: "#dc2626" }}
          >
            Drop
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditCampaignPage;